import { useState } from 'react';
import { Shield, Lock, Unlock, Bell, Camera, AlertTriangle, CheckCircle, Clock, Eye } from 'lucide-react';

interface SecurityZone {
  id: string;
  name: string;
  status: 'locked' | 'unlocked' | 'alert';
  type: 'door' | 'window' | 'gate';
  lastActivity: string;
}

interface SecurityEvent {
  id: string;
  type: 'entry' | 'exit' | 'alert' | 'system';
  message: string;
  timestamp: string;
  zone: string;
}

export default function HomeSecurity() {
  const [systemArmed, setSystemArmed] = useState(false);
  const [zones, setZones] = useState<SecurityZone[]>([
    { id: '1', name: 'Puerta Principal', status: 'locked', type: 'door', lastActivity: 'Hace 2 horas' },
    { id: '2', name: 'Puerta Trasera', status: 'locked', type: 'door', lastActivity: 'Hace 5 horas' },
    { id: '3', name: 'Ventana Sala', status: 'locked', type: 'window', lastActivity: 'Hace 1 día' },
    { id: '4', name: 'Ventana Cocina', status: 'unlocked', type: 'window', lastActivity: 'Hace 30 min' },
    { id: '5', name: 'Portón Garage', status: 'locked', type: 'gate', lastActivity: 'Hace 3 horas' },
  ]);

  const [events, setEvents] = useState<SecurityEvent[]>([
    { id: '1', type: 'entry', message: 'Puerta Principal desbloqueada', timestamp: '2026-03-17T08:30:00', zone: 'Puerta Principal' },
    { id: '2', type: 'system', message: 'Sistema armado automáticamente', timestamp: '2026-03-17T10:00:00', zone: 'Sistema' },
    { id: '3', type: 'exit', message: 'Portón Garage abierto', timestamp: '2026-03-17T09:15:00', zone: 'Portón Garage' },
  ]);

  const [showPanicMode, setShowPanicMode] = useState(false);

  const toggleZone = (id: string) => {
    setZones(zones.map(z => {
      if (z.id === id) {
        return { ...z, status: z.status === 'locked' ? 'unlocked' : 'locked', lastActivity: 'Justo ahora' };
      }
      return z;
    }));
  };

  const triggerPanic = () => {
    setShowPanicMode(true);
    // In real app, this would send alerts to security/emergency contacts
    setTimeout(() => setShowPanicMode(false), 5000);
  };

  const lockedCount = zones.filter(z => z.status === 'locked').length;
  const unlockedCount = zones.filter(z => z.status === 'unlocked').length;

  return (
    <div className="space-y-6">
      {/* Panic Alert Overlay */}
      {showPanicMode && (
        <div className="fixed inset-0 bg-red-600 z-50 flex items-center justify-center animate-pulse">
          <div className="text-center text-white">
            <AlertTriangle className="w-24 h-24 mx-auto mb-4" />
            <h1 className="text-4xl font-bold mb-2">¡ALERTA DE EMERGENCIA!</h1>
            <p className="text-xl">Contactando a contactos de emergencia...</p>
          </div>
        </div>
      )}

      {/* System Status */}
      <div className={`p-6 rounded-xl border-2 ${
        systemArmed 
          ? 'bg-green-50 dark:bg-green-900/20 border-green-500' 
          : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-500'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
              systemArmed ? 'bg-green-500' : 'bg-yellow-500'
            }`}>
              <Shield className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-[var(--v-text-primary)] dark:text-white">
                {systemArmed ? 'Sistema Armado' : 'Sistema Desarmado'}
              </h3>
              <p className="text-gray-500 dark:text-slate-400">
                {lockedCount} de {zones.length} zonas aseguradas
              </p>
            </div>
          </div>
          <button
            onClick={() => setSystemArmed(!systemArmed)}
            className={`px-6 py-3 rounded-lg font-bold transition ${
              systemArmed 
                ? 'bg-red-500 hover:bg-red-600 text-white' 
                : 'bg-green-500 hover:bg-green-600 text-white'
            }`}
          >
            {systemArmed ? 'Desarmar' : 'Armar'}
          </button>
        </div>
      </div>

      {/* Zones Grid */}
      <div>
        <h3 className="text-lg font-semibold text-[var(--v-text-primary)] dark:text-white mb-3 flex items-center gap-2">
          <Lock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          Zonas de Seguridad
        </h3>
        <div className="grid grid-cols-2 gap-3">
          {zones.map((zone) => (
            <button
              key={zone.id}
              onClick={() => toggleZone(zone.id)}
              className={`p-4 rounded-xl border text-left transition ${
                zone.status === 'locked' 
                  ? 'bg-[var(--v-surface-1)] border-[var(--v-surface-3)]' 
                  : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-500/50'
              }`}
            >
              <div className="flex items-start justify-between">
                {zone.status === 'locked' ? (
                  <Lock className="w-5 h-5 text-green-600 dark:text-green-400" />
                ) : (
                  <Unlock className="w-5 h-5 text-yellow-500 dark:text-yellow-400" />
                )}
                <span className={`text-xs px-2 py-1 rounded-full ${
                  zone.status === 'locked' 
                    ? 'bg-green-500/20 text-green-600 dark:text-green-400' 
                    : 'bg-yellow-500/20 text-yellow-500 dark:text-yellow-400'
                }`}>
                  {zone.status === 'locked' ? 'Cerrado' : 'Abierto'}
                </span>
              </div>
              <p className="text-[var(--v-text-primary)] dark:text-white font-medium mt-2">{zone.name}</p>
              <p className="text-gray-400 dark:text-slate-500 text-xs mt-1">{zone.lastActivity}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-3">
        <button 
          onClick={triggerPanic}
          className="bg-red-600 hover:bg-red-700 text-gray-900 dark:text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2"
        >
          <AlertTriangle className="w-5 h-5" />
          Botón de Pánico
        </button>
        <button className="bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-900 dark:text-white py-4 rounded-xl font-medium flex items-center justify-center gap-2">
          <Camera className="w-5 h-5" />
          Ver Cámaras
        </button>
      </div>

      {/* Recent Activity */}
      <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-surface-3)] overflow-hidden">
        <div className="p-4 border-b border-[var(--v-surface-3)]">
          <h3 className="font-semibold text-[var(--v-text-primary)] dark:text-white flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            Actividad Reciente
          </h3>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-slate-700">
          {events.map((event) => (
            <div key={event.id} className="p-4 flex items-center gap-3">
              {event.type === 'entry' && <Lock className="w-5 h-5 text-green-600 dark:text-green-400" />}
              {event.type === 'exit' && <Unlock className="w-5 h-5 text-yellow-500 dark:text-yellow-400" />}
              {event.type === 'alert' && <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />}
              {event.type === 'system' && <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />}
              <div className="flex-1">
                <p className="text-[var(--v-text-primary)] dark:text-white text-sm">{event.message}</p>
                <p className="text-gray-400 dark:text-slate-500 text-xs">{event.zone}</p>
              </div>
              <span className="text-gray-400 dark:text-slate-500 text-xs">
                {new Date(event.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Security Tips */}
      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-500/20">
        <h4 className="font-semibold text-[var(--v-text-primary)] dark:text-white mb-2 flex items-center gap-2">
          <Eye className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          Consejo de Seguridad
        </h4>
        <p className="text-gray-700 dark:text-slate-300 text-sm">
          Recuerda revisar que todas las puertas y ventanas estén cerradas antes de armar el sistema.
          Activa las notificaciones para recibir alertas en tiempo real.
        </p>
      </div>
    </div>
  );
}
